import * as Cesium from 'cesium';

/**
 * Höhen-Farbskalen ("Farbskala"-Einstellung).
 *
 * Jede Palette ist eine Liste normierter Stops (t = 0 … 1, niedrig → hoch)
 * mit RGB-Werten. Die tatsächliche Höhe (Meter) wird über ALT_MAX auf 0…1
 * abgebildet und zwischen den Stops linear interpoliert. Dieselben Stops
 * speisen sowohl die Cesium-Farben als auch die CSS-Gradienten (Legende +
 * Auswahl-Vorschau) — so bleibt alles garantiert konsistent.
 */
export const ALT_MAX = 10000; // m — darüber wird die höchste Farbe gehalten

export const PALETTES = {
  classic: {
    name: 'Standard',
    // Standard = grün oben (hoch), rot unten (niedrig)
    stops: [
      { t: 0,    rgb: [255, 59, 59] },  // rot (niedrig)
      { t: 0.35, rgb: [255, 140, 0] },  // orange
      { t: 0.70, rgb: [255, 212, 0] },  // gelb
      { t: 1,    rgb: [0, 210, 106] }   // grün (hoch)
    ]
  },
  inverted: {
    name: 'Umgekehrt',
    // Umgekehrt = rot oben (hoch), grün unten (niedrig)
    stops: [
      { t: 0,    rgb: [0, 210, 106] },  // grün (niedrig)
      { t: 0.30, rgb: [255, 212, 0] },  // gelb
      { t: 0.65, rgb: [255, 140, 0] },  // orange
      { t: 1,    rgb: [255, 59, 59] }   // rot (hoch)
    ]
  },
  violet: {
    name: 'Gelb-Lila',
    stops: [
      { t: 0,    rgb: [255, 209, 102] }, // warmes gelb
      { t: 0.50, rgb: [239, 111, 181] }, // pink
      { t: 1,    rgb: [106, 48, 147] }   // violett
    ]
  },
  aurora: {
    name: 'Aurora',
    stops: [
      { t: 0,    rgb: [0, 229, 176] },   // türkis
      { t: 0.35, rgb: [45, 156, 219] },  // blau
      { t: 0.70, rgb: [123, 91, 214] },  // violett
      { t: 1,    rgb: [255, 106, 213] }  // magenta
    ]
  },
  spectrum: {
    name: 'Spektrum',
    stops: [
      { t: 0,    rgb: [68, 1, 84] },     // tiefviolett
      { t: 0.25, rgb: [59, 82, 139] },   // indigo
      { t: 0.50, rgb: [33, 145, 140] },  // petrol
      { t: 0.75, rgb: [94, 201, 98] },   // grün
      { t: 1,    rgb: [253, 231, 37] }   // gelb
    ]
  }
};

const STORAGE_KEY = 'frm-palette';
const listeners = new Set();

let activePaletteId = loadInitialPalette();

function loadInitialPalette() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored && PALETTES[stored] ? stored : 'classic';
}

export function getPaletteId() {
  return activePaletteId;
}

/**
 * Aktive Farbskala wechseln. Leert den Farb-Cache und benachrichtigt
 * Abonnenten (Karte + Legende), damit sie sich sofort neu einfärben.
 */
export function setPalette(id) {
  if (!PALETTES[id] || id === activePaletteId) return;
  activePaletteId = id;
  localStorage.setItem(STORAGE_KEY, id);
  colorCache.clear();
  for (const listener of listeners) {
    try { listener(id); } catch (error) { console.warn('Palette listener failed:', error); }
  }
}

export function onPaletteChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function interpolateStops(stops, t) {
  t = Math.max(0, Math.min(1, t));
  const last = stops[stops.length - 1];
  if (t >= last.t) return last.rgb;
  for (let i = 1; i < stops.length; i++) {
    const hi = stops[i];
    if (t <= hi.t) {
      const lo = stops[i - 1];
      const f = (t - lo.t) / (hi.t - lo.t);
      return [
        Math.round(lo.rgb[0] + (hi.rgb[0] - lo.rgb[0]) * f),
        Math.round(lo.rgb[1] + (hi.rgb[1] - lo.rgb[1]) * f),
        Math.round(lo.rgb[2] + (hi.rgb[2] - lo.rgb[2]) * f)
      ];
    }
  }
  return last.rgb;
}

function rgbForAltitude(altitudeMeters) {
  const t = Math.max(0, altitudeMeters || 0) / ALT_MAX;
  return interpolateStops(PALETTES[activePaletteId].stops, t);
}

// getAltitudeColor runs per entity per frame — memoize on 50m buckets
// to avoid allocating a new Cesium.Color every call (cache cleared on palette change)
const colorCache = new Map();

/**
 * Get Cesium color for an altitude (meters) — active palette, continuous gradient.
 */
export function getAltitudeColor(altitudeMeters) {
  const bucket = Math.round(Math.max(0, altitudeMeters || 0) / 50);
  let color = colorCache.get(bucket);
  if (!color) {
    const [r, g, b] = rgbForAltitude(bucket * 50);
    color = new Cesium.Color(r / 255, g / 255, b / 255, 1.0);
    colorCache.set(bucket, color);
  }
  return color;
}

/**
 * Get CSS color string for altitude (for UI elements) — same active palette.
 */
export function getAltitudeColorCSS(altitudeMeters) {
  const [r, g, b] = rgbForAltitude(altitudeMeters);
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * CSS-Gradient einer Palette (für Legende + Auswahl-Vorschau).
 * direction 'to top' → t=1 (hohe Höhe) oben, t=0 unten.
 */
export function paletteGradientCSS(id = activePaletteId, direction = 'to top') {
  const stops = (PALETTES[id] || PALETTES.classic).stops;
  const parts = stops.map(
    (s) => `rgb(${s.rgb[0]}, ${s.rgb[1]}, ${s.rgb[2]}) ${Math.round(s.t * 100)}%`
  );
  return `linear-gradient(${direction}, ${parts.join(', ')})`;
}

/**
 * Get color based on flight phase
 */
export function getPhaseColor(phase) {
  switch (phase) {
    case 'ground':
      return Cesium.Color.fromCssColorString('#888888');
    case 'climbing':
      return Cesium.Color.fromCssColorString('#00ff88');
    case 'descending':
      return Cesium.Color.fromCssColorString('#ff8800');
    case 'cruising':
    default:
      return Cesium.Color.fromCssColorString('#4488ff');
  }
}

/**
 * Interpolate between two colors based on value
 * @param {number} value - Current value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {Cesium.Color} colorMin - Color at minimum
 * @param {Cesium.Color} colorMax - Color at maximum
 * @returns {Cesium.Color}
 */
export function interpolateColor(value, min, max, colorMin, colorMax) {
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  return Cesium.Color.lerp(colorMin, colorMax, t, new Cesium.Color());
}
